// v2 sea turtle — a true sea turtle gliding at the surface: a carapace dome over
// a pale flared rim with a row of raised scutes down the spine, a cream plastron
// below, long swept-back front flipper blades and short rear paddles, a neck
// carrying a beaked head with high-set eyes, and a stub tail. Two shells: v0
// olive-green (green turtle), v1 amber-brown (hawksbill). Ported 1:1 from
// age-of-mcp's v2/sea-turtle.ts, using the library's shared `wetland` (eyes)
// helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const SHELLS = [
  { dome: "#3e6e4a", scute: "#2f5a3a", rim: "#7a9a72", skin: "#5a8a5e",
    flip: "#4a7a52", belly: "#d8d2b0" },
  { dome: "#7a5a32", scute: "#5e431f", rim: "#b09058", skin: "#8a7a4e",
    flip: "#6e5c38", belly: "#e2d6ac" },
];

function seaTurtleParts(v: number): Part[] {
  const c = SHELLS[v % SHELLS.length]!;
  const parts: Part[] = [
    // pale flared rim under the dome
    { geometry: new THREE.CylinderGeometry(0.047, 0.052, 0.011, 7), color: c.rim,
      position: [0, 0.018, 0], scale: [1.15, 1, 0.95] },
    // carapace dome
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.dome,
      position: [0, 0.026, 0], scale: [1.25, 0.6, 1.05] },
    // cream plastron riding the waterline
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.belly,
      position: [0.004, 0.01, 0], scale: [1.3, 0.4, 1.0] },
    // neck, beaked head, eyes
    { geometry: new THREE.CylinderGeometry(0.009, 0.012, 0.022, 5), color: c.skin,
      position: [0.06, 0.022, 0], rotation: [0, 0, -1.35] },
    { geometry: new THREE.IcosahedronGeometry(0.0155, 0), color: c.skin,
      position: [0.077, 0.024, 0], scale: [1.3, 0.85, 0.9] },
    { geometry: new THREE.ConeGeometry(0.005, 0.011, 4), color: c.flip,
      position: [0.096, 0.021, 0], rotation: [0, 0, -Math.PI / 2] },
    ...wetland.eyes(0.082, 0.031, 0.011, 0.0035),
    // stub tail
    { geometry: new THREE.ConeGeometry(0.005, 0.014, 4), color: c.skin,
      position: [-0.06, 0.014, 0], rotation: [0, 0, Math.PI / 2] },
  ];
  // raised scute ridge down the spine
  for (const x of [0.022, -0.002, -0.026]) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.011, 0),
      color: c.scute, position: [x, 0.044, 0], scale: [1.2, 0.5, 1.1] });
  }
  // long swept-back front flipper blades
  for (const s of [1, -1]) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.02, 0),
      color: c.flip, position: [0.032, 0.014, s * 0.058],
      rotation: [0, -s * 0.47, s * 0.12], scale: [0.7, 0.2, 1.9] });
  }
  // short rear paddles
  for (const s of [1, -1]) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.013, 0),
      color: c.flip, position: [-0.042, 0.012, s * 0.042],
      rotation: [0, -s * 0.6, 0], scale: [0.6, 0.2, 1.3] });
  }
  return parts;
}

export const seaTurtleV2 = defineModel({
  id: "sea-turtle-v2",
  scale: 3.38, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Sea turtle (v2)",
  tags: ["creature","sea","turtle","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly sea turtle.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.1,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => seaTurtleParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => seaTurtleParts(1) },
  ],
  animations: [],
});
